/**
 * Product Card Block — opt into parent List's generic child-count context.
 *
 * List only exposes `data-list-child-count` / `--list-child-count`.
 * Maps that into `.product-card-block` local modifiers so stylesheet
 * rules never select `.list-block` or collide with theme `.product-card`.
 *
 * Clickable cards navigate from empty areas only. Native controls (links,
 * buttons, specs <details>) are excluded by event target, not stacking.
 *
 * MO specs <details> stay in product-card.js: cards in the same list ancestor
 * mirror open/closed. list.js does not know about specs.
 */
(function () {
  if (window.__mammotionProductCardJsBound) return;
  window.__mammotionProductCardJsBound = true;

  const COUNT_CLASS_PREFIX = 'product-card-block--list-count-';
  const LIST_CONTEXT_SELECTOR = '[data-list-child-count]';
  const SPECS_SELECTOR = '.product-card-block-info__specs-mo';
  const INTERACTIVE_SELECTOR =
    'a, button, input, textarea, select, label, summary, details, [data-product-card-ignore-click]';
  const specsSyncing = new WeakSet();

  /**
   * @param {HTMLElement} card
   */
  function applyListContext(card) {
    const list = card.closest(LIST_CONTEXT_SELECTOR);
    if (!list) return;

    const count = list.getAttribute('data-list-child-count') || '0';
    card.dataset.listSiblingCount = count;
    card.classList.add('product-card-block--in-list');
    [...card.classList]
      .filter((name) => name.startsWith(COUNT_CLASS_PREFIX))
      .forEach((name) => card.classList.remove(name));
    card.classList.add(`${COUNT_CLASS_PREFIX}${count}`);
  }

  /**
   * Whole-card navigation must not cover native controls.
   * Clicks on links, buttons, and specs <details> keep their own behavior.
   * @param {HTMLElement} card
   */
  function bindClickableCard(card) {
    if (!card.classList.contains('product-card-block--clickable')) return;
    if (card.dataset.cardClickBound === 'true') return;
    card.dataset.cardClickBound = 'true';

    card.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;

      const link = card.querySelector('[data-product-card-primary-link]');
      const href = link?.href || card.getAttribute('data-product-card-href');
      if (!href) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        window.open(href, '_blank', 'noopener');
        return;
      }

      if (link) {
        link.click();
        return;
      }

      if (card.getAttribute('data-link-target') === '_blank') {
        window.open(href, '_blank', 'noopener');
        return;
      }

      window.location.assign(href);
    });
  }

  /**
   * @param {ParentNode} [scope]
   */
  function initAll(scope) {
    const root = scope || document;
    root.querySelectorAll('.product-card-block').forEach((card) => {
      applyListContext(card);
      bindClickableCard(card);
    });
  }

  /**
   * Native `toggle` does not bubble. Capture it and mirror `open` onto sibling
   * specs in the same list. Cards outside a list stay independent.
   * @param {Event} event
   */
  function syncListSpecs(event) {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    if (!details.matches(SPECS_SELECTOR)) return;
    if (specsSyncing.has(details)) return;

    const list = details.closest(LIST_CONTEXT_SELECTOR);
    if (!list) return;

    const isOpen = details.open;
    list.querySelectorAll(SPECS_SELECTOR).forEach((el) => {
      if (!(el instanceof HTMLDetailsElement) || el === details || el.open === isOpen) return;
      specsSyncing.add(el);
      el.open = isOpen;
      specsSyncing.delete(el);
    });
  }

  document.addEventListener('toggle', syncListSpecs, true);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
